import { useAdminAuth } from "@/contexts/useAdminAuth";
import { defaultAdminPage } from "@/lib/admin-access";
import { Button } from "@a7y/tailwind-catalyst";
import { ShieldX, ServerCrash } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * `no-access` (default): the signed-in account is not an admin, or the API is
 * unreachable — a full-screen wall with Sign Out.
 * `out-of-scope` (A7Y-4957): an admin whose role does not cover this page —
 * rendered inside the admin shell, with a way back to the role's default page.
 */
export function AccessDeniedPage({ reason = "no-access" }: { reason?: "no-access" | "out-of-scope" }) {
  const { logout, authError, role } = useAdminAuth();
  const navigate = useNavigate();

  if (reason === "out-of-scope") {
    return (
      <div className="flex flex-col items-center px-4 py-24 text-center">
        <div className="mb-6 rounded-full bg-red-600/10 p-4">
          <ShieldX className="h-10 w-10 text-red-500" />
        </div>
        <h1 className="text-xl font-semibold text-zinc-950 dark:text-white">Not part of your access</h1>
        <p className="mt-3 max-w-md text-sm text-zinc-500 dark:text-zinc-400">
          Your admin role does not include this page. Ask a superadmin if you need it.
        </p>
        <Button onClick={() => navigate(defaultAdminPage(role))} color="zinc" className="mt-6">
          Back to your admin home
        </Button>
      </div>
    );
  }

  const isApiError = !!authError;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center">
          <div className={`rounded-full p-4 ${isApiError ? "bg-yellow-600/20" : "bg-red-600/20"}`}>
            {isApiError ? (
              <ServerCrash className="h-12 w-12 text-yellow-400" />
            ) : (
              <ShieldX className="h-12 w-12 text-red-400" />
            )}
          </div>
        </div>
        <h1 className="text-2xl font-bold text-white">
          {isApiError ? "API Unreachable" : "Access Denied"}
        </h1>
        <p className="mt-3 text-gray-400">
          {isApiError
            ? authError
            : "Your account does not have admin access. Contact an administrator if you believe this is an error."}
        </p>
        <Button
          onClick={logout}
          color="zinc"
          className="mt-6 w-full justify-center"
        >
          Sign Out
        </Button>
      </div>
    </div>
  );
}
